import { v4 } from 'uuid';
import { PastEvent, PaginationMetadata, ParticipantFilter } from '../../../API/types';
import { create } from 'zustand';
import { setAccumulatedItems } from '../utils';
import { ProviderIntegration } from '../../../redux/typings/store';
import { storeFilters } from '../../saved-filters/store';
import { getFilters } from '../../saved-filters/get';
import { removeCurrentView } from '../../saved-filters/remove-current-view';
import { setCurrentView } from '../../saved-filters/set-current-view';
import { SavedFiltersFilterItem } from '../../saved-filters/types';

export const PAST_MEETINGS_PAGE_KEY = 'past-meetings';

const defaultDateRange: [null, null] = [null, null];
const defaultMetadata = {
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
  totalPages: 0,
};

export type PastMeetingStore = ReturnType<typeof createPastMeetingsStore>;

interface PastMeetingsState {
  events: PastEvent[];
  isFetching: boolean;
  updateAccounts: ProviderIntegration[];
  contacts: string[];
  dateRange: [Date, Date] | [null, null];
  dateRangeType: string;
  paginationMetadata: PaginationMetadata;
  page: number;
  pageSize: number;
  searchTerm: string;
  meetingType: string;
  mType: string | null;
  showMineOnly: boolean;
  showReadyOnly: boolean;
  showOnlyUnmappedAccounts: boolean;
  canLoadData: boolean;
  selectedMeetingsTo: Record<string, string[]>;
  currentFilterView: string;
  participants: ParticipantFilter[];
  contactsFiltered: (contacts: string[]) => unknown;
  eventsFetched: (events: PastEvent[]) => unknown;
  fetchStatusChanged: (isFetching: boolean) => unknown;
  updateAccountsFiltered: (accounts: ProviderIntegration[]) => unknown;
  dateRangeChanged: (range: [Date, Date] | [null, null]) => unknown;
  dateRangeTypeChanged: (value: string) => void
  pageChanged: (value: number) => unknown;
  pageSizeChanged: (value: number) => unknown;
  paginationMetadataSet: (metadata: PaginationMetadata) => unknown;
  searchTermChanged: (value: string) => unknown;
  meetingTypeChanged: (value: string) => unknown;
  showMineOnlyChanged: (value: boolean) => unknown;
  showReadyOnlyChanged: (value: boolean) => unknown;
  showOnlyUnmappedAccountsChanged: (value: boolean) => unknown;
  onInsightChanged: (id: string, value: boolean) => unknown;
  resetFilters: () => unknown;
  keepFetching: () => unknown;
  refetch: () => unknown;
  reset: VoidFunction;
  deleteEvent: (id: string) => unknown;
  updateMeetingAccount: (
    id: string,
    account: { id: string; name: string; provider: string; mappingId: string } | null,
  ) => unknown;
  updateMType: (mType: string | null) => unknown;
  updateFiltersState: (filters: PersistFiltersType) => void;
  selectMeetingToRemove: (id: string) => void;
  clearSelectedMeetingsToRemove: VoidFunction;
  saveFiltersView: (viewName: string) => void;
  setCurrentFilterView: (viewId: string | null) => void;
  setParticipants: (participants: ParticipantFilter[]) => void;

  //Internal
  fetchId: string;
}

type PersistFiltersType = Record<string, [Date, Date] | [null, null] | string | boolean | number | null | ProviderIntegration[]>

const persistFilters = (newFilters: PersistFiltersType) => {
  const currentFiltersObject = JSON.parse(localStorage.getItem('pageFilters') || '{}');
  const currentPageFilters = currentFiltersObject[PAST_MEETINGS_PAGE_KEY] || {};
  const newFiltersObject = {
    ...currentPageFilters,
    ...newFilters,
  };
  currentFiltersObject[PAST_MEETINGS_PAGE_KEY] = newFiltersObject;
  localStorage.setItem('pageFilters', JSON.stringify(currentFiltersObject));
}

export const createPastMeetingsStore = () => {
  return create<PastMeetingsState>((set, get) => {
    return {
      events: [],
      isFetching: false,
      updateAccounts: [],
      contacts: [],
      dateRange: defaultDateRange,
      dateRangeType: '30d',
      canLoadData: false,
      paginationMetadata: defaultMetadata,
      page: 1,
      pageSize: 20,
      searchTerm: '',
      meetingType: 'all',
      mType: null,
      showMineOnly: false,
      showReadyOnly: false,
      showOnlyUnmappedAccounts: false,
      participants: [],
      selectedMeetingsTo: {
        remove: [],
      },
      currentFilterView: '',
      // Filters
      updateFiltersState: filters => set(filters),
      contactsFiltered: contacts => set({ contacts, page: 1, events: [] }),
      updateAccountsFiltered: accounts =>
        set(store => {
          if (store.updateAccounts.length !== accounts.length) {
            persistFilters({ updateAccounts: accounts });
            return { updateAccounts: accounts, page: 1, events: [] };
          } else {
            const set = new Set(accounts.map(account => account.id));
            for (const acc of store.updateAccounts) {
              if (set.has(acc.id)) {
                set.delete(acc.id);
              } else {
                set.add(acc.id);
              }
            }
            if (set.size !== 0) {
              persistFilters({ updateAccounts: accounts });
              return { updateAccounts: accounts, page: 1, events: [] }
            }
            return store;
          }
        }),
      dateRangeChanged: (range: [Date, Date] | [null, null]) => {
        set({ dateRange: range, page: 1, events: [] })
        persistFilters({ dateRange: range });
      },
      dateRangeTypeChanged: (value: string) => {
        set({ dateRangeType: value })
        persistFilters({ dateRangeType: value });
      },
      searchTermChanged: (value: string) => set({ searchTerm: value, page: 1, events: [] }),
      meetingTypeChanged: (value: string) => {
        set({ meetingType: value, page: 1, events: [] })
        persistFilters({ meetingType: value });
      },
      showMineOnlyChanged: (value: boolean) => {
        set({ showMineOnly: value, page: 1, events: [] })
        persistFilters({ showMineOnly: value });
      },
      showOnlyUnmappedAccountsChanged: (value: boolean) => {
        set({ showOnlyUnmappedAccounts: value, page: 1, events: [] })
        persistFilters({ showOnlyUnmappedAccounts: value });
      },
      showReadyOnlyChanged: (value: boolean) => {
        set({ showReadyOnly: value, page: 1, events: [] })
        persistFilters({ showReadyOnly: value });
      },
      resetFilters: () => {
        set({
          events: [],
          contacts: [],
          updateAccounts: [],
          showMineOnly: false,
          showReadyOnly: false,
          mType: null,
          meetingType: 'all',
          paginationMetadata: defaultMetadata,
          page: 1,
        })
        const currentFiltersObject = JSON.parse(localStorage.getItem('pageFilters') || '{}');
        delete currentFiltersObject[PAST_MEETINGS_PAGE_KEY];
        localStorage.setItem('pageFilters', JSON.stringify(currentFiltersObject));
      },

      pageChanged: (value: number) => set({ page: value }),
      pageSizeChanged: (value: number) => set({ pageSize: value }),
      paginationMetadataSet: metadata => set({ paginationMetadata: metadata }),

      keepFetching: () => {
        set(state => {
          if (state.paginationMetadata.page < state.paginationMetadata.totalPages) {
            return { page: state.page + 1 };
          }
          return state;
        });
      },
      eventsFetched: events => {
        set(state => {
          return { events: setAccumulatedItems(state.events, events) };
        });
      },
      refetch: () => {
        set({
          paginationMetadata: defaultMetadata,
          page: 1,
          fetchId: v4(),
          events: [],
        });
      },
      reset: () => {
        set({
          searchTerm: '',
          showMineOnly: false,
          showReadyOnly: false,
          meetingType: 'all',
          mType: null,
          updateAccounts: [],
          dateRangeType: '30d',
          dateRange: [null, null],
        })
        const currentFilters = JSON.parse(localStorage.getItem('pageFilters') || '{}');
        delete currentFilters[PAST_MEETINGS_PAGE_KEY];
        localStorage.setItem('pageFilters', JSON.stringify(currentFilters));
      },
      fetchStatusChanged: isFetching => set({ isFetching }),
      deleteEvent: id => {
        set(state => {
          return { events: state.events.filter(e => e.id !== id) };
        });
      },
      updateMeetingAccount: (id, account) =>
        set(state => {
          const event = state.events.find(event => event.id === id);
          if (event) event.account = account;
          return { events: state.events };
        }),
      onInsightChanged: (id: string, val: boolean) => {
        set(state => {
          const event = state.events.find(event => event.id === id);
          if (event) event.excludedFromInsights = val;
          return { events: state.events };
        });
      },
      updateMType: mType => {
        set({
          mType,
          page: 1,
          events: [],
        })
        persistFilters({ mType });
      },
      selectMeetingToRemove: id => {
        set(state => {
          const finalList = state.selectedMeetingsTo.remove.includes(id)
            ? state.selectedMeetingsTo.remove.filter(meetingId => meetingId !== id)
            : [...state.selectedMeetingsTo.remove, id];

          return {
            selectedMeetingsTo: {
              remove: finalList
            }
          };
        });
      },
      clearSelectedMeetingsToRemove: () => {
        set({
          selectedMeetingsTo: {
            remove: []
          }
        });
      },
      saveFiltersView: (viewName: string) => {
        const state = get();
        const filters: Record<string, SavedFiltersFilterItem> = {
          dateRange: {
            value: state.dateRange,
            label: 'Date Range',
            type: 'dateRange'
          },
          dateRangeType: {
            value: state.dateRangeType,
            label: 'Date Range Type',
            type: 'dateRangeType'
          },
          searchTerm: {
            value: state.searchTerm,
            label: 'Search Term',
            type: 'text'
          },
          showMineOnly: {
            value: state.showMineOnly,
            label: 'Show Mine Only',
            type: 'boolean'
          },
          showReadyOnly: {
            value: state.showReadyOnly,
            label: 'Show Ready Only',
            type: 'boolean'
          },
          showOnlyUnmappedAccounts: {
            value: state.showOnlyUnmappedAccounts,
            label: 'Show Only Unmapped Accounts',
            type: 'boolean'
          },
          meetingType: {
            value: state.meetingType,
            label: 'Meeting Type',
            type: 'text'
          },
          mType: {
            value: state.mType,
            label: 'Invitee Scope',
            type: 'text'
          },
        }

        storeFilters({ pageKey: PAST_MEETINGS_PAGE_KEY, pageTitle: 'Past Meetings Dashboard', viewName, filters });
      },
      setCurrentFilterView: (viewId: string | null) => {
        if (!viewId) {
          removeCurrentView(PAST_MEETINGS_PAGE_KEY);
          const { refetch, reset } = get()
          if (refetch) refetch();
          if (reset) reset();
          set({
            currentFilterView: '',
          });
          return;
        }

        const filters = getFilters({ pageKey: PAST_MEETINGS_PAGE_KEY, viewId });
        setCurrentView(PAST_MEETINGS_PAGE_KEY, viewId);

        const { refetch } = get()
        if (refetch) refetch();

        set({
          ...filters,
          currentFilterView: viewId,
        })
      },
      setParticipants: participants => set({ participants, page: 1, events: [] }),
      // Internal
      fetchId: v4(),
    };
  });
};